import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { PrivatePageLayout, PageHeader, PageTableCard } from '../../components/PrivatePage';
import SelectSymbol from '../../components/SelectSymbol/SelectSymbol';
import Pagination from '../../components/Pagination/Pagination';
import Toast from '../../components/Toast/Toast';
import SharedStrategies from '../Strategies/SharedStrategies/SharedStrategies';
import AutomationModal from '../Automations/AutomationModal/AutomationModal';
import GridModal from '../Automations/GridModal/GridModal';
import BotRow from './BotRow';
import CreateBotButton from './CreateBotButton';
import { getBots, startBot, stopBot, deleteBot, loadAutomationForEdit } from '../../services/BotsService';
import { isGridBot } from './botUtils';

const BOT_TYPES = ['', 'ALGO', 'GRID', 'SCHEDULED', 'CONDITION'];

const DEFAULT_AUTOMATION = {
    symbol: '',
    conditions: '',
    actions: [],
    name: '',
    indexes: '',
};

function Bots() {
    const history = useHistory();
    const location = useLocation();
    const automationModalTriggerRef = useRef(null);
    const gridModalTriggerRef = useRef(null);

    function getPage(loc = location) {
        return new URLSearchParams(loc.search).get('page');
    }

    const [symbol, setSymbol] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [bots, setBots] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(getPage() || 1);
    const [notification, setNotification] = useState({ type: '', text: '' });
    const [editAutomation, setEditAutomation] = useState(DEFAULT_AUTOMATION);

    useEffect(() => {
        return history.listen((loc) => {
            setPage(getPage(loc) || 1);
        });
    }, [history]);

    function fetchBots() {
        getBots(symbol, page || 1, typeFilter)
            .then((result) => {
                setBots(result.rows);
                setCount(result.count);
            })
            .catch((err) => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }

    useEffect(() => {
        fetchBots();
    }, [page, symbol, typeFilter]);

    function resetToFirstPage() {
        setPage(1);
        history.push('/bots?page=1');
    }

    function onSymbolChange(event) {
        setSymbol(event.target.value);
        resetToFirstPage();
    }

    function onTypeFilterChange(event) {
        setTypeFilter(event.target.value);
        resetToFirstPage();
    }

    function findBotFromEvent(event) {
        const rawId = event.target.id.replace(/^(edit|stop|start|delete)/, '');
        const sourceId = parseInt(rawId, 10);
        return bots.find((bot) => bot.sourceId === sourceId);
    }

    function onEditClick(event, bot) {
        loadAutomationForEdit(bot)
            .then((automation) => {
                setEditAutomation(automation);
                if (isGridBot(bot)) {
                    gridModalTriggerRef.current?.click();
                } else {
                    automationModalTriggerRef.current?.click();
                }
            })
            .catch((err) => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }

    function onStopClick(event) {
        const bot = findBotFromEvent(event);
        if (!bot) return;
        stopBot(bot)
            .then(() => fetchBots())
            .catch((err) => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }

    function onStartClick(event) {
        const bot = findBotFromEvent(event);
        if (!bot) return;
        startBot(bot)
            .then(() => fetchBots())
            .catch((err) => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }

    function onDeleteClick(event) {
        const bot = findBotFromEvent(event);
        if (!bot) return;
        deleteBot(bot)
            .then(() => fetchBots())
            .catch((err) => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }

    function onNewAutomationClick(event) {
        if (event.target.id === 'linkSchedule') {
            setEditAutomation({ ...DEFAULT_AUTOMATION, schedule: new Date().toISOString() });
        } else {
            setEditAutomation({ ...DEFAULT_AUTOMATION });
        }
    }

    function onAutomationSubmit() {
        fetchBots();
    }

    return (
        <>
            <PrivatePageLayout>
                <PageHeader
                    eyebrow="Currently running"
                    title="Bots"
                    subtitle="Manage algo strategies and standalone automations in one place."
                    actions={(
                        <div className="private-page-header-filters">
                            <select
                                className="form-select form-select-sm"
                                value={typeFilter}
                                onChange={onTypeFilterChange}
                                aria-label="Filter by bot type"
                            >
                                <option value="">All types</option>
                                {BOT_TYPES.filter(Boolean).map((type) => (
                                    <option key={type} value={type}>{type.charAt(0) + type.slice(1).toLowerCase()}</option>
                                ))}
                            </select>
                            <div className="header-filter-symbol">
                                <SelectSymbol
                                    symbol={symbol}
                                    disabled={false}
                                    onChange={onSymbolChange}
                                    showAny={false}
                                    onlyFavorites={false}
                                    fullWidth
                                />
                            </div>
                            <CreateBotButton onNewAutomationClick={onNewAutomationClick} />
                        </div>
                    )}
                />

                <div className="private-page-section-header">
                    <h2 className="private-page-section-title">Your bots</h2>
                </div>

                <PageTableCard>
                    <div className="table-wrapper table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th className="border-gray-200">Type</th>
                                    <th className="border-gray-200">Symbol</th>
                                    <th className="border-gray-200">Name</th>
                                    <th className="border-gray-200">Composition</th>
                                    <th className="border-gray-200">Status</th>
                                    <th className="border-gray-200">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bots && bots.length ? (
                                    bots.map((bot) => (
                                        <BotRow
                                            key={bot.id}
                                            bot={bot}
                                            onEditClick={onEditClick}
                                            onStartClick={onStartClick}
                                            onStopClick={onStopClick}
                                            onDeleteClick={onDeleteClick}
                                        />
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="text-muted text-center py-4">
                                            No bots found. Create one using the button above.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <Pagination count={count} />
                </PageTableCard>

                <SharedStrategies symbol={symbol} onNotification={setNotification} />
            </PrivatePageLayout>

            <button
                ref={automationModalTriggerRef}
                type="button"
                className="d-none"
                data-bs-toggle="modal"
                data-bs-target="#modalAutomation"
                aria-hidden="true"
                tabIndex={-1}
            />
            <button
                ref={gridModalTriggerRef}
                type="button"
                className="d-none"
                data-bs-toggle="modal"
                data-bs-target="#modalGrid"
                aria-hidden="true"
                tabIndex={-1}
            />

            <AutomationModal data={editAutomation} onSubmit={onAutomationSubmit} />
            <GridModal data={editAutomation} onSubmit={onAutomationSubmit} />
            <Toast type={notification.type} text={notification.text} />
        </>
    );
}

export default Bots;